import React, { useState } from "react";
import useReveal from "../hooks/useReveal";
import PageHero from "../components/PageHero";
import { SERVICES, FAQS } from "../mock";
import { useSettings } from "../context/SettingsContext";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Plus, Minus, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

export default function ContactPage() {
  useReveal();
  const settings = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", service: "", message: "" });
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const [open, setOpen] = useState(0);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setError("");
    try {
      await api.submitEnquiry(form);
      setState("sent");
      setForm({ name: "", email: "", phone: "", postcode: "", service: "", message: "" });
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.detail || "Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <div className="page-enter">
      <PageHero
        variant="light"
        eyebrow="Contact — begin the conversation"
        title="Start the"
        italic="conversation."
        subtitle="Book a complimentary consultation. Share your project details and our team will prepare a refined proposal tailored to your space and requirements. and return a fully costed proposal."
        image="https://images.unsplash.com/photo-1752594756894-bcf378884a09?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400"
      />

      <section className="bg-bone py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7 rv">
            <div className="border border-line bg-bone p-8 lg:p-12">
              <div className="text-eyebrow text-amber mb-5">— Enquiry form</div>
              <h2 className="font-display-tight text-4xl lg:text-5xl text-ink mb-10">Tell us about your project.</h2>

              {state === "sent" ? (
                <div className="flex items-start gap-4 bg-ink text-bone p-8">
                  <CheckCircle2 className="w-8 h-8 text-amber shrink-0" style={{ fill: "transparent" }} />
                  <div>
                    <div className="font-display text-2xl text-bone mb-2">Enquiry received.</div>
                    <p className="text-bone/75">A senior partner will be in touch within one working day to arrange your complimentary consultation. A copy has been sent to {settings?.email}.</p>
                    <button onClick={() => setState("idle")} className="mt-5 text-eyebrow text-amber link-line">Send another →</button>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *"
                    className="border border-line bg-transparent px-4 py-4 text-ink placeholder-ink/45 focus:border-amber focus:outline-none transition-colors" />
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address *"
                    className="border border-line bg-transparent px-4 py-4 text-ink placeholder-ink/45 focus:border-amber focus:outline-none transition-colors" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number"
                    className="border border-line bg-transparent px-4 py-4 text-ink placeholder-ink/45 focus:border-amber focus:outline-none transition-colors" />
                  <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Postcode"
                    className="border border-line bg-transparent px-4 py-4 text-ink placeholder-ink/45 focus:border-amber focus:outline-none transition-colors" />
                  <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
                    className="md:col-span-2 border border-line bg-transparent px-4 py-4 text-ink focus:border-amber focus:outline-none transition-colors">
                    <option value="">Select a service — optional</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                  </select>
                  <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your project — size, budget, timeline, goals… *"
                    className="md:col-span-2 border border-line bg-transparent px-4 py-4 text-ink placeholder-ink/45 focus:border-amber focus:outline-none transition-colors resize-none" />

                  {state === "error" && (
                    <div className="md:col-span-2 flex items-start gap-3 border border-red-400/40 bg-red-500/5 text-red-700 px-4 py-3">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}

                  <button type="submit" disabled={state === "sending"}
                    className="md:col-span-2 group btn-magnetic bg-ink text-bone py-5 text-[12px] tracking-[0.25em] uppercase font-bold hover:bg-amber hover:text-ink transition-colors flex items-center justify-center gap-3 disabled:opacity-60">
                    {state === "sending" ? "Sending…" : "Submit Enquiry"}
                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 rv space-y-5">
            <div className="bg-ink text-bone p-8">
              <div className="text-eyebrow text-amber mb-5">—Main Branch</div>
              <h3 className="font-display text-3xl text-bone mb-6">Greetland, UK </h3>
              <div className="space-y-4 text-bone/80 text-[15px]">
                <div className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-1 text-amber shrink-0" /><div>{settings?.address_line1}<br />{settings?.address_line2}<br />{settings?.address_line3}<br />{settings?.address_city}</div></div>
                <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-amber shrink-0" /><div><a href={`tel:${settings?.phone}`} className="hover:text-amber">{settings?.phone}</a> · <a href={`tel:${settings?.phone_alt}`} className="hover:text-amber">{settings?.phone_alt}</a></div></div>
                <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-amber shrink-0" /><a href={`mailto:${settings?.email}`} className="hover:text-amber">{settings?.email}</a></div>
                <div className="flex items-start gap-3"><Clock className="w-4 h-4 mt-1 text-amber shrink-0" /><div>{settings?.hours_weekdays}<br />{settings?.hours_saturday}<br />{settings?.hours_sunday}</div></div>
              </div>
            </div>

            <div className="aspect-[4/3] overflow-hidden bg-stone-l">
              <iframe
  title="Studio"
  src={`https://maps.google.com/maps?q=${encodeURIComponent(
    `${settings?.address_line1 || ""} ${settings?.address_line2 || ""} ${settings?.address_line3 || ""} ${settings?.address_city || ""}`
  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
  className="w-full h-full border-0 grayscale contrast-75"
  loading="lazy"
/>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-bone-2 py-24 lg:py-32">
        <div className="max-w-[1000px] mx-auto px-6">
          <div className="text-center rv mb-14">
            <div className="text-eyebrow text-amber mb-5">— Frequently asked</div>
            <h2 className="font-display-tight text-5xl md:text-6xl text-ink text-balance">Your questions, <span className="italic font-display">answered.</span></h2>
          </div>
          <div className="divide-y divide-line border-y border-line">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <button key={i} onClick={() => setOpen(isOpen ? -1 : i)} className="w-full text-left py-6 lg:py-8 flex items-start gap-6 group">
                  <span className="text-eyebrow text-amber shrink-0 w-10">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-6">
                      <span className="font-display text-2xl md:text-3xl text-ink group-hover:text-amber transition-colors">{f.q}</span>
                      {isOpen ? <Minus className="w-5 h-5 text-amber shrink-0" /> : <Plus className="w-5 h-5 text-ink/50 group-hover:text-amber shrink-0 transition-colors" />}
                    </div>
                    <div className={`grid transition-all duration-500 ${isOpen ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden"><p className="text-ink-soft leading-relaxed pr-8 text-[15px]">{f.a}</p></div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
